import { useEffect, useRef, useCallback } from "react";
import { EntityDetectionService } from "../utils/entityDetection";
import { useChatContext } from "../ChatProvider";
import { updateEntities } from "../store/chatSlice";
import { EntityDetectionRequest, EntityType } from "../types";

export const useEntityDetection = () => {
  const { dispatch, currentSessionId, sessions, urls, requestParams } = useChatContext();

  const entityServiceRef = useRef<EntityDetectionService | null>(null);
  const currentSessionIdRef = useRef(currentSessionId);
  const sessionsRef = useRef(sessions);

  // Update refs on every render
  currentSessionIdRef.current = currentSessionId;
  sessionsRef.current = sessions;

  // Initialize entity detection service
  useEffect(() => {
    entityServiceRef.current = new EntityDetectionService(500); // 500ms debounce

    return () => {
      entityServiceRef.current?.cleanup();
    };
  }, []);

  const detectEntities = useCallback(
    async (text: string) => {
      if (!entityServiceRef.current || !urls?.entityDetectionUrl) {
        return;
      }

      // Get current session data from refs at call time
      const sessionId = currentSessionIdRef.current;
      if (!sessionId) return;

      const currentContext = sessionsRef.current[sessionId]?.context;
      const existingEntities = (currentContext?.entities || []) as { name: string }[];
      const previouslyDetectedEntities = existingEntities.map(entity => entity.name);

      const request: EntityDetectionRequest = {
        text,
        detected_entities:
          previouslyDetectedEntities.length > 0 ? previouslyDetectedEntities : undefined,
      };

      try {
        const response = await entityServiceRef.current.detectEntities(
          `${urls.origin}${urls.entityDetectionUrl}`,
          request,
          requestParams
        );

        if (response && sessionId) {
          // Get existing entities to preserve selected_type for entities that already exist
          const currentContext = sessionsRef.current[sessionId]?.context;
          const existingEntities = (currentContext?.entities || []) as {
            name: string;
            selected_type?: EntityType;
          }[];
          const existingEntityMap = new Map(existingEntities.map(e => [e.name, e.selected_type]));

          // Set selected_type to existing value or first type in types_found for new entities
          const entitiesWithSelectedType = response.entities.map(entity => ({
            ...entity,
            selected_type:
              existingEntityMap.get(entity.name) || entity.types_found[0] || EntityType.UNKNOWN,
          }));

          // Update entities in the store
          dispatch(
            updateEntities({
              sessionId,
              entities: entitiesWithSelectedType,
            })
          );
        }
      } catch (error) {
        console.error("Entity detection failed:", error);
      }
    },
    [urls?.entityDetectionUrl, urls?.origin, requestParams, dispatch]
  );

  return { detectEntities };
};
